/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { HallowElementProps, MaskProps } from "@packages/ui";
import {
  CloseIcon,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  emotionDynamics,
  numericPercentages,
  ScrollArea,
  spacing,
  Text,
  useDragDropTouch,
} from "@packages/ui";
import type { MouseEventHandler, ReactNode } from "react";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import type { DropOptions } from "react-aria";
import { useDrop } from "react-aria";

const defaultContainerStyle: Interpolation = {
  display: "grid",
  gridTemplateAreas: '"current" "queue" "controls"',
  gridTemplateColumns: "1fr",
  gridTemplateRows: "min-content minmax(0, 1fr) min-content",
  height: numericPercentages[100],
  maxWidth: "640px",
  overflow: "hidden",
  width: "calc(100vw - 48px)", // 24px per side on the media player
};
const scrollAreaContainerStyle: Interpolation = {
  height: numericPercentages[100],
};

const defaultTitleStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  margin: 0,
  padding: spacing.s,
  position: "sticky",
  textTransform: "uppercase",
  top: 0,
  zIndex: 1,
};

const defaultListStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  width: "calc(100vw - 48px)",
  [cssMediaQueryMinWidthTablet]: { width: numericPercentages[100] },
  zIndex: 0,
};
const endListListStyle: Interpolation = {
  paddingBottom: "calc(100% - 96px)", // 84px = height of one queue item, 12px = generous gap
};
const containerListStyle: Interpolation = {
  position: "relative",
  width: "calc(100vw - 48px)",
  [cssMediaQueryMinWidthTablet]: { width: numericPercentages[100] },
};
const emptyTextListStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  display: "flex",
  height: "60vh",
  placeContent: "center",
  placeItems: "center",
  width: numericPercentages[100],
};

const defaultDeleterStyle: Interpolation = {
  display: "flex",
  height: numericPercentages[100],
  left: 0,
  placeContent: "center",
  placeItems: "center",
  position: "absolute",
  top: 0,
  width: "10%",
};

const defaultControlsStyle: Interpolation = {
  columnGap: spacing.s,
  display: "grid",
  gridArea: "controls",
  gridAutoColumns: "min-content",
  gridTemplateAreas: '"back play forward"',
  gridTemplateRows: "1fr",
  placeContent: "center",
  placeItems: "center",
};

type QueueSection = {
  title: string;
  items: ReactNode[];
};

export type UIQueueProps = HallowElementProps<"div"> & {
  past: QueueSection;
  future: QueueSection;
  current: ReactNode;
  onDelete?: DropOptions["onDrop"];
  isDragging?: boolean;
  titleBgCSS?: Interpolation;
  masksColor?: MaskProps["color"];
};

export const UIQueue = forwardRef<HTMLDivElement, UIQueueProps>(
  (
    {
      past,
      current,
      future,
      isDragging,
      onDelete,
      children,
      titleBgCSS = {},
      masksColor,
      ...props
    },
    ref,
  ) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const midRef = useRef<HTMLHeadingElement>(null);
    const deleteRef = useRef<HTMLDivElement>(null);
    const { dropProps } = useDrop({ onDrop: onDelete, ref: deleteRef });
    const t = useTranslations();
    useDragDropTouch();

    const handleDragScroll: MouseEventHandler = useCallback(
      (e) => {
        const box = e.currentTarget.getBoundingClientRect();

        if (e.clientY > box.bottom - Math.min(100, box.height / 10)) {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollBy({
              top: Math.min(25, box.height / 40),
              behavior: "instant",
            });
          });
        } else if (e.clientY < box.top + Math.min(50, box.height / 20)) {
          requestAnimationFrame(() => {
            scrollRef.current?.scrollBy({
              top: -Math.min(25, box.height / 40),
              behavior: "instant",
            });
          });
        }
      },
      [scrollRef?.current],
    );

    useEffect(() => {
      requestAnimationFrame(() => {
        if (scrollRef.current)
          scrollRef.current.scrollTop = midRef.current?.offsetTop ?? 0;
      });
    }, [scrollRef.current, midRef.current]);

    return (
      <div ref={ref} css={defaultContainerStyle} {...props}>
        <ul css={defaultListStyle}>{current}</ul>
        <div css={containerListStyle} onDragOver={handleDragScroll}>
          <ScrollArea
            viewportProps={{ ref: scrollRef }}
            horizontal={false}
            masksSize="xs"
            maskBottom
            masksColor={masksColor}
            css={scrollAreaContainerStyle}
          >
            {past.items?.length ? (
              <>
                <Text
                  as={"h2"}
                  type={"title"}
                  size={"s"}
                  css={[defaultTitleStyle, titleBgCSS]}
                >
                  {past.title}
                </Text>
                <ul css={defaultListStyle}>{past.items}</ul>
              </>
            ) : null}
            <Text
              ref={midRef}
              as={"h2"}
              type={"title"}
              size={"s"}
              css={[defaultTitleStyle, titleBgCSS]}
            >
              {future.title}
            </Text>
            {future.items?.length ? (
              <ul css={[defaultListStyle, endListListStyle]}>{future.items}</ul>
            ) : (
              <Text
                as={"div"}
                type={"body"}
                size={"l"}
                css={emptyTextListStyle}
              >
                {t("queue_empty_state_web")}
              </Text>
            )}
          </ScrollArea>
          {isDragging ? (
            <div
              ref={deleteRef}
              css={[
                defaultDeleterStyle,
                emotionDynamics.backgroundGradient({
                  direction: "to left",
                  red: 180,
                  green: 30,
                  blue: 30,
                }),
                emotionDynamics.mask({
                  directions: ["to top", "to bottom"],
                  spreads: [50, 50],
                }),
              ]}
              {...dropProps}
            >
              <CloseIcon />
            </div>
          ) : null}
        </div>
        <div css={defaultControlsStyle}>{children}</div>
      </div>
    );
  },
);

UIQueue.displayName = "UIQueue";
